/** 功能设置文章：每篇正文只放 2 条不重复的内链 */
export const FEATURES_POSTS = {
	'schedule-messages-gb-whatsapp': {
		slug: 'schedule-messages-gb-whatsapp',
		categoryId: 'features-settings',
		title: 'How to Schedule Messages in GB WhatsApp',
		description:
			'Schedule a GB WhatsApp Pro message when your build has the option, and see why a timed message may never leave the phone.',
		keywords: 'schedule messages gb whatsapp',
		h1: 'How to Schedule Messages in GB WhatsApp',
		lead:
			'Scheduling, when your build has it, queues a message for later. It still needs the app running and a network near that time. Menus are not the same on every version.',
		body: `
      <h2>Where to set it</h2>
      <p>Open the chat. Look for Schedule, Message scheduler, or a clock in the attachment menu. Names change by build. Pick a time at least a few minutes ahead so the queue can save it. If you do not see any of those words, this version does not include scheduling. Updating does not guarantee the menu will appear.</p>
      <p>Turn off battery optimization for GB WhatsApp Pro if you need a message to leave overnight. A phone that sleeps hard can hold the text until you open the app, which is later than the time you picked.</p>

      <h2>Check or cancel a queued message</h2>
      <p>Some builds list pending items under GB settings or when you long-press the send area. Others only let you cancel and write it again. Send a test to yourself a few minutes out before you rely on it for someone else.</p>
      <p>If the time passes and the message sits on the clock like a normal unsent text, the queue is not the only issue. Treat it as <a href="/gb-whatsapp-messages-not-sending/">a GB WhatsApp message that never leaves the phone</a>: connection, date and time, then battery limits.</p>

      <h2>If nothing sends while the screen is off</h2>
      <p>Android can block background work even when the scheduler inside the app is correct. Allow notifications and unrestricted battery use. After that, open the app once near the send time if the phone is aggressive about sleep. Scheduling cannot send while the phone is powered off.</p>
      <p>Notifications that never appear, even for normal new chats, point at the system settings rather than the clock you set. Check <a href="/help/notifications-not-working/">GB WhatsApp notifications that stay silent</a> before you delete the scheduled text and start over.</p>
    `,
		faqs: [
			['Will it send if the phone is off?', 'No. The app has to be able to run close to the time you chose.'],
		],
		publishedAt: '2026-11-01',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Scheduling',
	},
	'lock-individual-chats-gb-whatsapp': {
		slug: 'lock-individual-chats-gb-whatsapp',
		categoryId: 'features-settings',
		title: 'How to Lock Individual Chats in GB WhatsApp',
		description:
			'Lock one GB WhatsApp Pro chat with a PIN or fingerprint, and see what that lock does not hide.',
		keywords: 'lock individual chats gb whatsapp',
		h1: 'How to Lock Individual Chats in GB WhatsApp',
		lead:
			'Chat lock stops someone who has your unlocked phone from opening one thread. It is not the same as hiding last seen, and it is not official WhatsApp’s Chat Lock.',
		body: `
      <h2>Turn it on</h2>
      <p>Long-press the conversation, or open GB Settings and look for Privacy → Chat lock. The label varies. Set a PIN you do not reuse from banking apps. Use fingerprint only if the phone itself already has a lock screen.</p>
      <p>Some builds hide locked chats until you search the name or open a locked-chats folder. Others leave the chat in the list and ask for the PIN when you tap it. Try both so you know which one you have.</p>

      <h2>If you cannot find per-chat lock</h2>
      <p>Your build may only lock the whole app, or the option may have been removed. Search settings for “lock” before you assume it is under a different menu. An app-wide PIN is not the same feature. The difference is laid out under <a href="/features/chat-features/">GB WhatsApp chat tools, including app lock versus one chat</a>.</p>
      <p>If the phone was updated and fingerprint stops working, the Android fingerprint data may have reset. Enter the PIN, then enroll the fingerprint again. Do not clear GB WhatsApp data to fix a PIN prompt.</p>

      <h2>What the lock does not do</h2>
      <p>Notifications can still show the message on the lock screen. Turn off notification previews in Android if the text itself should stay hidden. Backups are not encrypted just because a chat has a PIN. Someone with the backup file can still have the messages.</p>
      <p>If your goal is to stop people seeing that you are online, locking a thread does not do that. That control is separate: <a href="/features/privacy-features/">hiding last seen and online status in GB WhatsApp Pro</a>.</p>
    `,
		faqs: [
			['Will a locked chat still notify?', 'Usually yes. Mute the chat or hide notification text on the lock screen if the preview is the problem.'],
		],
		publishedAt: '2026-11-02',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Chat Lock',
	},
	'hide-blue-ticks-gb-whatsapp': {
		slug: 'hide-blue-ticks-gb-whatsapp',
		categoryId: 'features-settings',
		title: 'How to Hide Blue Ticks in GB WhatsApp',
		description:
			'Find GB WhatsApp Pro read-receipt settings, see what blue ticks mean, and why they can still show in groups.',
		keywords: 'hide blue ticks gb whatsapp',
		h1: 'How to Hide Blue Ticks in GB WhatsApp',
		lead:
			'Two blue ticks mean the other person opened the chat. GB WhatsApp Pro may let you stop sending that signal. The menu name is not the same on every build.',
		body: `
      <h2>What the ticks mean</h2>
      <p>One grey tick: the message reached WhatsApp. Two grey ticks: it reached their phone. Two blue ticks: they opened the chat. Hiding blue ticks does not hide the fact that you replied, sent a voice note, or came online. People can still infer that you saw something.</p>

      <h2>Where the switch is</h2>
      <p>Open GB Settings → Privacy. Look for Hide blue ticks, Second tick, Hide seen, or Disable read receipts. Change one switch, then ask someone you trust to send you a message and tell you what they see. Do not flip every privacy toggle at once or you will not know which one did the work.</p>
      <p>On some builds, turning receipts off also hides other people’s blue ticks from you. Read the line under the switch. If you only wanted last seen changed, leave this toggle alone and use <a href="/features/privacy-features/">last seen and online status settings in GB WhatsApp Pro</a> instead.</p>

      <h2>When blue ticks still show</h2>
      <p>Groups, broadcasts, and status viewers often ignore the one-to-one switch. A voice call also shows that you are available. Test in a small group before you rely on it there.</p>
      <p>These switches only change what your client sends. They do not remove you from backups or from WhatsApp’s own systems. What a modified app still cannot hide is part of <a href="/safety/privacy-risks/">privacy limits that remain when you use GB WhatsApp Pro</a>.</p>
    `,
		faqs: [
			['Can I hide ticks for one person only?', 'Some builds have a per-contact switch. Others apply the setting to every chat. Check Privacy before you assume it is per person.'],
		],
		publishedAt: '2026-11-03',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Read Receipts',
	},
	'use-gb-whatsapp-dnd-mode': {
		slug: 'use-gb-whatsapp-dnd-mode',
		categoryId: 'features-settings',
		title: 'How to Use DND Mode in GB WhatsApp',
		description:
			'GB WhatsApp Pro DND mode, when your version has it, is not Android Do Not Disturb. See what it pauses and how to turn it off.',
		keywords: 'gb whatsapp dnd mode',
		h1: 'How to Use DND Mode in GB WhatsApp',
		lead:
			'DND inside GB WhatsApp Pro is an in-app switch. Android’s Do Not Disturb is a separate system setting. Both can be on at once, which makes notifications confusing.',
		body: `
      <h2>What it changes</h2>
      <p>Depending on the build, GB DND may freeze how you appear online, quiet notifications, or hold messages until you turn it off. It does not always block WhatsApp calls, and people in groups may still see activity. Send a test from a second phone before you count on it for a meeting.</p>

      <h2>Turn it on and off</h2>
      <p>Look in GB Settings under Universal, Other, or Privacy for DND, or a label like freeze last seen. Some versions add a shortcut on the chat list after you enable it once. Use the same switch to turn it off. If a banner stays on screen, tap that banner.</p>
      <p>After you turn it off, stay on Wi‑Fi for a minute so held messages can arrive. If the phone stays silent for ordinary chats too, Android notifications are off as well. That is <a href="/help/notifications-not-working/">GB WhatsApp notifications that do not appear after DND is off</a>, not another tap on the DND switch.</p>

      <h2>The option is missing</h2>
      <p>If settings have no DND wording, your build does not include it. Android’s own Do Not Disturb will still silence sounds, but it will not change last seen inside WhatsApp. Battery restrictions can also stop messages while you think DND is off.</p>
      <p>When DND is off and new messages still never show up in the chat list, the problem is delivery, not the switch. Check <a href="/help/messages-not-receiving/">GB WhatsApp messages that do not arrive</a> before you reinstall.</p>
    `,
		faqs: [
			['Does GB DND silence Android alarms?', 'No. It only affects what this app does. Android Do Not Disturb is the system control for sounds.'],
		],
		publishedAt: '2026-11-04',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'DND Mode',
	},
	'download-gb-whatsapp-status-videos': {
		slug: 'download-gb-whatsapp-status-videos',
		categoryId: 'features-settings',
		title: 'How to Save GB WhatsApp Status Videos',
		description:
			'Save a GB WhatsApp Pro status video when the button is there, find the file afterward, and respect the other person’s privacy.',
		keywords: 'download gb whatsapp status videos',
		h1: 'How to Save GB WhatsApp Status Videos',
		lead:
			'If your build includes status saving, watch the status to the end, then tap save. A missing button is not the same problem as a save that fails.',
		body: `
      <h2>Save one status</h2>
      <ol class="article-steps">
        <li><strong>Open the status and let it finish.</strong> Some builds hide save until the bar completes.</li>
        <li><strong>Tap the download or save icon</strong> near the reply field.</li>
        <li><strong>Allow photos and videos</strong> if Android asks. On Android 13 and newer, approve both.</li>
      </ol>
      <p>Do this before the status expires. After 24 hours the story is gone and there is nothing to store. Saving a clip for yourself is not the same as posting it somewhere else. Ask before you share someone else’s status.</p>

      <h2>Find the file</h2>
      <p>Gallery apps hide GB folders. Open the file manager and sort by today’s date. The path is often under Android/media or a GB folder, and it changes by phone brand. Save a short test status first so you recognize the folder. More on where chat files land is in <a href="/features/media-sharing/">where GB WhatsApp Pro stores photos and videos you save</a>.</p>

      <h2>The tap does nothing</h2>
      <p>Low storage, a denied permission, or a status that already expired are the usual reasons. Free space and check the permission switches. If the icon is missing entirely, this version may have dropped the feature. Failed saves, as opposed to the steps above, are handled in <a href="/gb-whatsapp-status-download-not-working/">when a GB WhatsApp status will not download</a>.</p>
    `,
		faqs: [
			['Does the contact get notified that I saved it?', 'It depends on the build. Treat the file as private either way, and do not repost it without asking.'],
		],
		publishedAt: '2026-11-05',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Status Videos',
	},
}
